import { useState, useEffect, ReactElement } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AdminService, useApiEffect, useApiGetter } from '@localstack/services';

import { Box, Divider, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DecorativeDivider } from '@localstack/ui';

import { AdminPermission } from '@localstack/types';

import { AdminLayout } from '~/layouts';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { IdentityLogContent } from '~/views/customer/IdentityLog/IdentityLogContent';

import { BasicDetails } from './BasicDetails';
import { Subscriptions } from './Subscriptions';
import { PaymentMethods } from './PaymentMethods';
import { MembersAndLicensesPage } from './Members';
import { AuditTrail } from './AuditTrail';
import { UsageInsights } from './UsageInsights';
import { Actions } from './Actions';

const TabsDivider = styled(Divider)(({ theme }) => ({
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
}));

enum OrganizationTab {
  BASIC_DETAILS = 'basic-details',
  SUBSCRIPTIONS = 'subscriptions',
  MEMBERS_AND_LICENSES = 'members-and-licenses',
  AUDIT_TRAIL = 'audit-trail',
  IDENTITY_LOG = 'identity-log',
  USAGE = 'usage',
  DANGER_ZONE = 'danger-zone',
}

export const Organization = (): ReactElement => {
  const [queryParams, setQueryParams] = useSearchParams();

  const { id: orgId } = useParams<'id'>() as { id: string };

  const { can } = useAuthProvider();

  const [currentTab, setCurrentTab] = useState<OrganizationTab>(OrganizationTab.BASIC_DETAILS);
  const { data: organization } = useApiGetter(AdminService, 'getOrganization', [orgId]);
  const { listIdentityEvents } = useApiEffect(AdminService, ['listIdentityEvents']);

  useEffect(() => {
    const tab = queryParams.get('tab');

    if (tab) {
      setCurrentTab(tab as OrganizationTab);
    }
  }, []);

  useEffect(() => {
    queryParams.set('tab', currentTab);
    setQueryParams(queryParams);
  }, [currentTab]);

  return (
    <AdminLayout documentTitle="Organization Details" title={organization?.name ?? orgId}>
      <Tabs
        value={currentTab}
        onChange={(_, tab: OrganizationTab) => setCurrentTab(tab)}
        indicatorColor="primary"
        textColor="primary"
        sx={{ marginTop: '-16px' }}
      >
        <Tab value={OrganizationTab.BASIC_DETAILS} label="Basic Details" />
        <Tab value={OrganizationTab.SUBSCRIPTIONS} label="Subscriptions" />
        <Tab value={OrganizationTab.MEMBERS_AND_LICENSES} label="Members & Licenses" />
        <Tab value={OrganizationTab.AUDIT_TRAIL} label="Audit Trail" />
        <Tab value={OrganizationTab.IDENTITY_LOG} label="Identity Log" />
        <Tab value={OrganizationTab.USAGE} label="Usage" />
        {can(AdminPermission.WORKSPACES_WRITE) && <Tab value={OrganizationTab.DANGER_ZONE} label="Danger Zone" />}
      </Tabs>
      <TabsDivider />
      {currentTab === OrganizationTab.BASIC_DETAILS && (
        <Box mt={2}>
          <BasicDetails orgId={orgId} />
        </Box>
      )}
      {currentTab === OrganizationTab.SUBSCRIPTIONS && (
        <>
          <Box mt={2}>
            <Subscriptions orgId={orgId} />
          </Box>
          <Box mt={2} mb={2}>
            <DecorativeDivider />
          </Box>
          <Box mt={2}>
            <PaymentMethods orgId={orgId} />
          </Box>
        </>
      )}
      {currentTab === OrganizationTab.MEMBERS_AND_LICENSES && (
        <Box mt={2} flexGrow={1}>
          <MembersAndLicensesPage orgId={orgId} />
        </Box>
      )}
      {currentTab === OrganizationTab.AUDIT_TRAIL && (
        <Box mt={2}>
          <AuditTrail orgId={orgId} />
        </Box>
      )}
      {currentTab === OrganizationTab.IDENTITY_LOG && (
        <Box mt={2}>
          <IdentityLogContent orgId={orgId} fetchFn={listIdentityEvents} />
        </Box>
      )}
      {currentTab === OrganizationTab.USAGE && (
        <Box mt={2}>
          <UsageInsights orgId={orgId} />
        </Box>
      )}
      {can(AdminPermission.WORKSPACES_WRITE) && currentTab === OrganizationTab.DANGER_ZONE && (
        <Box mt={2}>
          <Actions orgId={orgId} />
        </Box>
      )}
    </AdminLayout>
  );
};
